import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Info } from 'lucide-react';

import { api } from '../lib/api';
import { useAuth } from '../auth/AuthContext';
import { formatCount, formatCurrency } from '../lib/format';
import { Delta, ErrorNote, Panel, Spinner } from '../components/ui';
import { HourlyChart, type HourlyPoint } from '../components/HourlyChart';
import { RankedBars } from '../components/RankedBars';
import { RangePicker } from '../components/RangePicker';
import { preset, rangeKey, rangeQuery, type RangeValue } from '../lib/range';

type GrossProfit =
  | { available: true; value: string; basis: Record<string, string> }
  | { available: false; reason: string; missingSnapshotDates: string[] };

interface Kpi {
  value: string | null;
  previous: string | null;
  changePercent: string | null;
}

interface OverviewResponse {
  window: {
    label: string;
    previousLabel: string;
    isPartial: boolean;
    timezone: string;
  };
  kpis: {
    netSales: Kpi;
    baskets: Kpi;
    averageBasket: Kpi;
    grossProfit: { current: GrossProfit; previous: GrossProfit; changePercent: string | null };
  };
  returns: { amount: string; count: number };
  unitsSold: number;
  hourly: HourlyPoint[];
  departments: { category: string; netSales: string; units: number; share: string | null }[];
  tender: { method: string; amount: string; count: number }[];
  // `rate` arrives display-ready ("20.00%" or "Exempt") and `amount` is
  // VAT-inclusive — do not append a % or add the two columns together.
  vat: { rate: string; amount: string; vat: string }[];
  topProducts: { productId: number; name: string; units: number; netSales: string }[];
  snapshotCoverage: { earliest: string | null; latest: string | null; days: number };
}

export function Overview(): React.JSX.Element {
  const { activeStore } = useAuth();
  const [range, setRange] = useState<RangeValue>(preset('today'));

  const { data, isPending, error } = useQuery({
    queryKey: ['overview', activeStore?.storeId, rangeKey(range)],
    queryFn: () =>
      api.get<OverviewResponse>(`/stores/${activeStore!.storeId}/overview?${rangeQuery(range)}`),
    enabled: Boolean(activeStore),
  });

  if (!activeStore) return <p className="qs-panel p-4 text-xs">No store selected.</p>;

  return (
    <>
      {/* The store name lives in the header switcher, so repeating it as the
          page heading wastes the most prominent line on something already on
          screen. The period is what changes, so the period leads. */}
      <header className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="qs-eyebrow">Overview</p>
          <h1 className="text-lg font-medium tracking-tight">
            {data?.window.label ?? 'Loading'}
          </h1>
        </div>
        <RangePicker value={range} onChange={setRange} />
      </header>

      {error && <ErrorNote error={error} />}

      {isPending && <Spinner label="Loading figures…" />}

      {data && (
        <>

          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            <KpiTile
              label="Net sales"
              value={data.kpis.netSales.value ? formatCurrency(data.kpis.netSales.value) : '—'}
              delta={data.kpis.netSales.changePercent}
              comparison={data.window.previousLabel}
            />
            <KpiTile
              label="Baskets"
              value={formatCount(data.kpis.baskets.value ?? '0')}
              delta={data.kpis.baskets.changePercent}
              comparison={data.window.previousLabel}
            />
            <KpiTile
              label="Average basket"
              value={
                data.kpis.averageBasket.value
                  ? formatCurrency(data.kpis.averageBasket.value)
                  : '—'
              }
              delta={data.kpis.averageBasket.changePercent}
              comparison={data.window.previousLabel}
            />
            <GrossProfitTile
              profit={data.kpis.grossProfit.current}
              delta={data.kpis.grossProfit.changePercent}
              comparison={data.window.previousLabel}
              coverage={data.snapshotCoverage}
            />
          </div>

          {/* A period with no trade produced six panels each saying "nothing to
              show", which reads as a broken page rather than a quiet day. One
              statement is clearer than six. */}
          {data.kpis.baskets.value === '0' && data.departments.length === 0 ? (
            <Panel className="mt-3">
              <div className="px-3 py-10 text-center">
                <p className="text-sm">No trade recorded in this period.</p>
                <p className="mt-1 text-xs text-ink-muted">
                  Try a wider range, or check the till has synced.
                </p>
              </div>
            </Panel>
          ) : (
          <>
          <div className="mt-3 grid gap-3 lg:grid-cols-[3fr_2fr]">
            <Panel title="Trading through the day">
              <HourlyChart points={data.hourly} />
            </Panel>

            <Panel title="By tender">
              <RankedBars
                caption="Takings by payment method, net of refunds"
                // The API returns all seven of the till's buckets including
                // empty ones, which Genie needs so a £0.00 reads as "none"
                // rather than "not measured". On a chart they are just four
                // flat bars, so untouched buckets are dropped here.
                rows={data.tender
                  .filter((row) => Number(row.amount) !== 0 || row.count > 0)
                  .map((row) => ({
                    label: row.method,
                    amount: row.amount,
                    detail: `${row.count}`,
                  }))}
              />
            </Panel>
          </div>

          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            <Panel title="By department">
              <RankedBars
                caption="Net sales by department"
                rows={data.departments.map((row) => ({
                  label: row.category,
                  amount: row.netSales,
                  detail: row.share ? `${row.share}%` : undefined,
                }))}
              />
            </Panel>

            <Panel title="Top products">
              {/* Ranked by units — see `getTopProducts`. Takings move to the
                  secondary column rather than being dropped: the pair together
                  is what tells you a fast line is also worth selling. */}
              <RankedBars
                caption="Best selling products by units sold"
                measure="count"
                rows={data.topProducts.map((row) => ({
                  label: row.name,
                  amount: String(row.units),
                  detail: formatCurrency(row.netSales),
                }))}
              />
            </Panel>
          </div>

          <div className="mt-3 grid gap-3 lg:grid-cols-2">
            <Panel title="VAT">
              <table className="w-full">
                <caption className="sr-only">
                  Takings including VAT, and the VAT within them, by rate
                </caption>
                <thead>
                  <tr className="border-b border-rule">
                    <th className="qs-eyebrow py-1.5 pl-3 text-left">Rate</th>
                    <th className="qs-eyebrow py-1.5 text-right">Incl. VAT</th>
                    <th className="qs-eyebrow py-1.5 pr-3 text-right">of which VAT</th>
                  </tr>
                </thead>
                <tbody>
                  {data.vat.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="py-6 text-center text-xs text-ink-faint">
                        No VAT recorded in this period.
                      </td>
                    </tr>
                  ) : (
                    data.vat.map((row) => (
                      <tr key={row.rate} className="border-b border-rule last:border-0">
                        <td className="qs-figure py-1.5 pl-3 text-xs">{row.rate}</td>
                        <td className="qs-figure py-1.5 text-right text-xs">
                          {formatCurrency(row.amount)}
                        </td>
                        <td className="qs-figure py-1.5 pr-3 text-right text-xs">
                          {formatCurrency(row.vat)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </Panel>

            <Panel title="Returns and volume">
              <dl className="divide-y divide-rule">
                <Row term="Returns" value={formatCurrency(data.returns.amount)} />
                <Row term="Return transactions" value={formatCount(data.returns.count)} />
                <Row term="Units sold" value={formatCount(data.unitsSold)} />
              </dl>
            </Panel>
          </div>
          </>
          )}
        </>
      )}
    </>
  );
}

function KpiTile({
  label,
  value,
  delta,
  comparison,
}: {
  label: string;
  value: string;
  delta: string | null;
  comparison: string;
}): React.JSX.Element {
  return (
    <div className="qs-panel p-3">
      <p className="qs-eyebrow">{label}</p>
      <p className="qs-figure mt-1.5 text-[length:var(--text-figure)] leading-none font-medium">
        {value}
      </p>
      <p className="mt-2 flex items-baseline gap-1.5">
        <Delta percent={delta} />
        <span className="text-2xs text-ink-faint">{comparison}</span>
      </p>
    </div>
  );
}

/**
 * Gross profit, or an explanation of why there isn't one.
 *
 * The API returns available/unavailable rather than a nullable number precisely
 * so this component cannot render a blank and let the reader assume zero. On
 * dev01, six of thirteen months of trading have no inventory snapshot, and the
 * till's own query would report those periods as a profit inflated by the entire
 * value of the shop's stock.
 */
function GrossProfitTile({
  profit,
  delta,
  comparison,
  coverage,
}: {
  profit: GrossProfit;
  delta: string | null;
  comparison: string;
  coverage: { earliest: string | null; latest: string | null };
}): React.JSX.Element {
  if (!profit.available) {
    return (
      <div className="qs-panel border-warn/30 bg-warn-soft p-3">
        <p className="qs-eyebrow text-warn">Gross profit</p>
        <p className="mt-1.5 flex items-center gap-1.5 text-sm font-medium text-warn">
          <Info size={14} aria-hidden />
          Not available
        </p>
        <p className="mt-1.5 text-2xs leading-relaxed text-warn">
          {profit.reason}
          {coverage.earliest && (
            <>
              {' '}
              Snapshots run from {coverage.earliest} to {coverage.latest}.
            </>
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="qs-panel p-3">
      <p className="qs-eyebrow">Gross profit</p>
      <p className="qs-figure mt-1.5 text-[length:var(--text-figure)] leading-none font-medium">
        {formatCurrency(profit.value)}
      </p>
      <p className="mt-2 flex items-baseline gap-1.5">
        <Delta percent={delta} />
        <span className="text-2xs text-ink-faint">{comparison}</span>
      </p>
    </div>
  );
}

function Row({ term, value }: { term: string; value: string }): React.JSX.Element {
  return (
    <div className="flex items-baseline justify-between px-3 py-1.5">
      <dt className="text-xs text-ink-muted">{term}</dt>
      <dd className="qs-figure text-xs">{value}</dd>
    </div>
  );
}
